//this class intercepts ever web service API call to do a couple things, like:
// - tell the LoaderService that a call is active, which the spinner component will use to show the spinner.
// - get the security token from every response header and save it in sessionStorage.
// - put the security token into every request header.
// - catch errors from the server and handle them consistently.
import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpResponse, HttpRequest, HttpHandler, HttpInterceptor } from '@angular/common/http';
import { LoaderService } from './loader.service';
import { tap, catchError, finalize } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { MessageBoxComponent } from '../message-box/message-box.component';

@Injectable()
export class LoaderInterceptor implements HttpInterceptor {
  private totalRequests: number = 0;

  constructor(private loaderService: LoaderService) { }

  intercept(request: HttpRequest<any>, next: HttpHandler) {
    //Gilmer: I looked at a bunch of different examples of how to do this online, and they were all a bit different. This one made the most sense to me. You have the totalRequests variable in case there's more than one at a time. You increase totalRequests when the request is generated and decrease it when the web call is finished.
    //Gilmer: A number of examples I looked at said you'd know when to hide the spinner because the response comes back or there's an error. But another example I saw showed that you can do it in the finalize. That's why there's a decreaseRequests() call in two places that are commented out in case we need to go back to that.
    this.totalRequests++;
    this.loaderService.isLoading = true;
    //To add a header for the security token to the request, we have to clone the request object because it is immutable. See https://angular.io/guide/http#immutability. We store the validation token in SessionStorage. If it's there, clone the request and add the token to the headers. If it's not there (e.g. we are on the login page so we don't have a token yet), then just use the request without cloning.
    let clonedRequest: HttpRequest<any>;
    const valToken: string = sessionStorage.validationToken;
    if (valToken && valToken.length > 0)
      clonedRequest = request.clone({ setHeaders: { ValidationToken: valToken } });
    else
      clonedRequest = request;

    return next.handle(clonedRequest).pipe(
      tap(res => {
        if (res instanceof HttpResponse) {
          // If there's a security token in the response headers, save it in sessionStorage to pass back with the next request.
          const token: string = res.headers.get("ValidationToken");
          if (token)
            sessionStorage.validationToken = token;
          //this.decreaseRequests();
        }
      }),
      catchError((error: HttpErrorResponse) => {
        let isAuthorizationError: boolean = false;
        //this.decreaseRequests();
        let errMsg = '';
        if (error.error instanceof ErrorEvent) {
          // Client Side Error (???Gilmer: I copied this from somewhere and am not sure it's right)
          errMsg = `Client Error: ${error.error.message}`;
        }
        else {
          // Server Side Error
          errMsg = `Server Error. Code: ${error.status},  Message: ${error.message}`;
          if (error.error && error.error.message)
            errMsg += ' ---- Inner Error: ' + error.error.message;
          if (error.status == 401) {
            isAuthorizationError = true;
            sessionStorage.clear();
            sessionStorage.logoutReason = "AuthorizationError";
            window.location.assign(document.getElementsByTagName('base')[0].href + "login");
          }
        }
        //???I don't know if this is what we want to do.
        if (!isAuthorizationError) {
          if (MessageBoxComponent.staticInstance)
            MessageBoxComponent.staticInstance.show("Error connecting to the server", errMsg, false);
          else
            console.log(errMsg);
        }
        return throwError(errMsg);
      }),
      finalize(() => {
        this.decreaseRequests();
      })
    );
  }

  private decreaseRequests() {
    this.totalRequests--;
    if (this.totalRequests === 0) {
      this.loaderService.isLoading = false;
    }
  }
}
